import { Field, Radio, Label as ZenLabel } from '@zendeskgarden/react-forms'
import { useMemo, useState } from 'react'

import {
    AvailabilityFilter,
    Duration,
    emptyDurationSelection,
    getDurationFromDurationSelection,
    getDurationSelectionFromDurationValue,
} from '../../utils/calendar'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import { Select } from '../common/fields/Select'
import { SelectOption, durationDayOptions, durationOptions } from '../settings/Options'

type Props = {
    availabilityFilter: AvailabilityFilter
    isOpen: boolean
    itemType?: string
    onDone: (duration: Duration) => void
    onClose: () => void
}

export const CalendarDurationModal = ({
    availabilityFilter,
    isOpen,
    itemType,
    onDone,
    onClose,
}: Props) => {
    const [durationSelection, setDurationSelection] = useState(
        getDurationSelectionFromDurationValue(availabilityFilter.duration.value)
    )

    const [durationType, setDurationType] = useState(
        itemType === 'reservation' ||
            !!durationSelection.days.value ||
            !!durationSelection.weeks.value
            ? 'weeks-days'
            : 'hours-mins'
    )

    const isDurationSet = useMemo(() => {
        const { hours, minutes, days, weeks } = durationSelection
        return !!hours.value || !!minutes.value || !!days.value || !!weeks.value
    }, [durationSelection])

    const dayOptions = useMemo(
        () =>
            !durationSelection.weeks.value ? durationDayOptions : durationDayOptions.slice(0, 6),
        [durationSelection.weeks.value]
    )

    const handleOnChange = (value: string) => {
        setDurationType(value)
        setDurationSelection(emptyDurationSelection)
    }

    const handleHoursSelection = (hours: SelectOption) => {
        setDurationSelection((prev) => ({
            ...prev,
            minutes: hours.label === '12 hours' ? emptyDurationSelection.minutes : prev.minutes,
            hours,
        }))
    }

    const handleWeeksSelection = (weeks: SelectOption) => {
        setDurationSelection((prev) => ({
            ...prev,
            days: weeks.label === '10 weeks' ? emptyDurationSelection.days : prev.days,
            weeks,
        }))
    }

    return (
        <Modal
            title="Duration"
            isOpen={isOpen}
            onClose={onClose}
            footer={
                <div className="flex w-full gap-4">
                    <Button
                        className="mr-auto"
                        disabled={!isDurationSet}
                        onClick={() => setDurationSelection(emptyDurationSelection)}
                    >
                        Clear
                    </Button>

                    <Button onClick={onClose}>Cancel</Button>

                    <Button
                        intent="primary"
                        onClick={() => onDone(getDurationFromDurationSelection(durationSelection))}
                    >
                        Done
                    </Button>
                </div>
            }
        >
            <div className="flex flex-col gap-4">
                {!itemType && (
                    <Field>
                        <Radio
                            name="duration-selection"
                            value="hours-mins"
                            checked={durationType === 'hours-mins'}
                            onChange={(e) => handleOnChange(e.target.value)}
                        >
                            <ZenLabel>
                                <Label className="text-gray-700">Hours & Mins</Label>
                            </ZenLabel>
                        </Radio>
                    </Field>
                )}

                {durationType === 'hours-mins' && (
                    <div className="flex gap-3">
                        <Select
                            options={durationOptions.map((option) => ({
                                id: option.value,
                                title: option.label,
                            }))}
                            selectedOptionID={durationSelection.hours.value}
                            onSelectOption={(option) =>
                                handleHoursSelection({ value: option.id, label: option.title })
                            }
                        />
                    </div>
                )}

                {!itemType && (
                    <Field>
                        <Radio
                            name="duration-selection"
                            value="weeks-days"
                            checked={durationType === 'weeks-days'}
                            onChange={(e) => handleOnChange(e.target.value)}
                        >
                            <ZenLabel>
                                <Label className="text-gray-700">Weeks & Days</Label>
                            </ZenLabel>
                        </Radio>
                    </Field>
                )}

                {durationType === 'weeks-days' && (
                    <div className="flex gap-3">
                        <Select
                            options={durationDayOptions.map((option) => ({
                                id: option.value,
                                title: option.label,
                            }))}
                            selectedOptionID={durationSelection.weeks.value}
                            onSelectOption={(option) =>
                                handleWeeksSelection({ value: option.id, label: option.title })
                            }
                        />
                    </div>
                )}
            </div>
        </Modal>
    )
}
